import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Progress, Space, Tag, Typography, message } from 'antd';
import { RobotOutlined, ShoppingCartOutlined, ThunderboltOutlined } from '@ant-design/icons';
import Cart from '../Header/Cart';
import { useTranslation } from '../../hooks/useTranslation';
import { buildDefaultHtmlFileName } from '../../utils/htmlFileName';
import {
  WebHostingAiApiError,
  WebHostingAiCapability,
  consumeWebHostingAiTask,
  getWebHostingAiCapability,
  getWebHostingAiTaskStatus,
  submitWebHostingAiTask,
} from '../../services/webHostingAi';
import {
  WebHostingAiDraft,
  getWebHostingAiDraft,
  saveWebHostingAiDraft,
} from '../../utils/webHostingAiDraft';
import './index.css';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;
const POLL_INTERVAL_MS = 1000;
// Cover the switch's maximum 600-second upstream timeout plus scheduling overhead.
const MAX_POLL_ATTEMPTS = 660;

interface WebHostingAiModalProps {
  open: boolean;
  isVip: boolean;
  capability?: WebHostingAiCapability;
  onCapabilityChange: (capability?: WebHostingAiCapability) => void;
  onCancel: () => void;
  onGenerated: (draft: WebHostingAiDraft) => void;
}

const TEMPLATE_KEYS = ['portfolio', 'product', 'campaign', 'resume'] as const;

const WebHostingAiModal: React.FC<WebHostingAiModalProps> = ({
  open,
  isVip,
  capability,
  onCapabilityChange,
  onCancel,
  onGenerated,
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pollAttempt, setPollAttempt] = useState(0);
  const [liveCode, setLiveCode] = useState('');
  const [liveReasoning, setLiveReasoning] = useState('');
  const [showCart, setShowCart] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);
  const reasoningRef = useRef<HTMLPreElement | null>(null);

  const clearPolling = useCallback(() => {
    generationRef.current += 1;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const refreshCapability = useCallback(() => {
    getWebHostingAiCapability()
      .then(onCapabilityChange)
      .catch(() => undefined);
  }, [onCapabilityChange]);

  useEffect(() => {
    if (open) {
      clearPolling();
      setPrompt('');
      setGenerating(false);
      setErrorMessage('');
      setPollAttempt(0);
      setLiveCode('');
      setLiveReasoning('');
      refreshCapability();
    }
    return clearPolling;
  }, [open, clearPolling, refreshCapability]);

  useEffect(() => {
    if (reasoningRef.current) {
      reasoningRef.current.scrollTop = reasoningRef.current.scrollHeight;
    }
  }, [liveReasoning]);

  const resolveTaskError = (code?: string) => {
    if (!code) {
      return t('webHostingAi.errors.taskFailed');
    }
    const key = code.startsWith('webHostingAi.') ? code.substring('webHostingAi.'.length) : 'taskFailed';
    const translated = t(`webHostingAi.errors.${key}`);
    return translated === `webHostingAi.errors.${key}`
      ? t('webHostingAi.errors.taskFailed')
      : translated;
  };

  const storeResult = async (taskId: string, html: string, normalizedPrompt: string) => {
    const draft: WebHostingAiDraft = {
      html,
      fileName: buildDefaultHtmlFileName(),
      prompt: normalizedPrompt,
      createdAt: Date.now(),
    };
    try {
      saveWebHostingAiDraft(draft);
    } catch {
      setGenerating(false);
      setErrorMessage(t('webHostingAi.localDraftSaveFailed'));
      return;
    }
    await consumeWebHostingAiTask(taskId).catch(() => undefined);
    setGenerating(false);
    message.success(t('webHostingAi.generateSuccess'));
    onGenerated(draft);
  };

  const pollTask = async (taskId: string, normalizedPrompt: string, attempt: number, generation: number) => {
    if (generation !== generationRef.current) {
      return;
    }
    if (attempt >= MAX_POLL_ATTEMPTS) {
      setGenerating(false);
      setErrorMessage(t('webHostingAi.errors.pollTimeout'));
      return;
    }
    try {
      const status = await getWebHostingAiTaskStatus(taskId);
      if (generation !== generationRef.current) {
        return;
      }
      setPollAttempt(attempt + 1);
      if (status.previewCode) {
        setLiveCode(status.previewCode);
      }
      if (status.reasoningPreview) {
        setLiveReasoning(status.reasoningPreview);
      }
      if (status.status === 'success' && status.result?.html) {
        await storeResult(taskId, status.result.html, normalizedPrompt);
        return;
      }
      if (status.status === 'failed') {
        setGenerating(false);
        setErrorMessage(resolveTaskError(status.errorCode));
        return;
      }
      pollTimerRef.current = setTimeout(
        () => pollTask(taskId, normalizedPrompt, attempt + 1, generation),
        POLL_INTERVAL_MS,
      );
    } catch (error) {
      if (generation !== generationRef.current) {
        return;
      }
      setGenerating(false);
      setErrorMessage(error instanceof Error ? error.message : t('webHostingAi.errors.taskFailed'));
    }
  };

  const submitGeneration = async () => {
    const normalizedPrompt = prompt.trim();
    if (!normalizedPrompt) {
      message.warning(t('webHostingAi.promptRequired'));
      return;
    }
    clearPolling();
    setGenerating(true);
    setErrorMessage('');
    setPollAttempt(0);
    setLiveCode('');
    setLiveReasoning('');
    const generation = generationRef.current;
    try {
      const accepted = await submitWebHostingAiTask(normalizedPrompt);
      if (capability) {
        onCapabilityChange({
          ...capability,
          remaining: Math.max(0, capability.remaining - 1),
        });
      }
      refreshCapability();
      await pollTask(accepted.taskId, normalizedPrompt, 0, generation);
    } catch (error) {
      setGenerating(false);
      setErrorMessage(error instanceof WebHostingAiApiError || error instanceof Error
        ? error.message
        : t('webHostingAi.errors.taskFailed'));
    }
  };

  const handleGenerate = () => {
    if (!isVip) {
      setShowCart(true);
      return;
    }
    if (getWebHostingAiDraft()) {
      Modal.confirm({
        title: t('webHostingAi.replaceDraftTitle'),
        content: t('webHostingAi.replaceDraftDescription'),
        okText: t('webHostingAi.replaceDraftConfirm'),
        cancelText: t('common.cancel'),
        onOk: submitGeneration,
      });
      return;
    }
    void submitGeneration();
  };

  const maxPromptChars = capability?.promptMaxChars || 2000;
  const exhausted = Boolean(capability && capability.remaining <= 0);

  return (
    <>
      <Modal
        title={(
          <Space>
            <RobotOutlined />
            <span>{t('webHostingAi.title')}</span>
            <Tag color="gold">VIP</Tag>
          </Space>
        )}
        open={open}
        width={720}
        closable={!generating}
        maskClosable={!generating}
        keyboard={!generating}
        onCancel={() => !generating && onCancel()}
        footer={[
          <Button key="cancel" disabled={generating} onClick={onCancel}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="generate"
            type="primary"
            icon={isVip ? <ThunderboltOutlined /> : <ShoppingCartOutlined />}
            loading={generating}
            disabled={generating || (isVip && (!prompt.trim() || exhausted))}
            onClick={handleGenerate}
          >
            {isVip ? t('webHostingAi.generateButton') : t('webHostingAi.openVip')}
          </Button>,
        ]}
      >
        {!isVip ? (
          <Alert
            type="warning"
            showIcon
            message={t('webHostingAi.vipRequiredTitle')}
            description={t('webHostingAi.vipRequiredDescription')}
          />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert type="info" showIcon message={t('webHostingAi.notice')} />
            {capability && (
              <div className="web-hosting-ai-quota">
                <Text type="secondary">{t('webHostingAi.dailyQuota')}</Text>
                <Tag color={capability.remaining > 0 ? 'green' : 'red'}>
                  {t('webHostingAi.dailyQuotaValue', {
                    remaining: capability.remaining,
                    limit: capability.dailyLimit,
                  })}
                </Tag>
              </div>
            )}
            <div>
              <Text strong>{t('webHostingAi.templates')}</Text>
              <div className="web-hosting-ai-templates">
                {TEMPLATE_KEYS.map((key) => (
                  <Button
                    key={key}
                    size="small"
                    disabled={generating}
                    onClick={() => setPrompt(t(`webHostingAi.templatePrompts.${key}`))}
                  >
                    {t(`webHostingAi.templateNames.${key}`)}
                  </Button>
                ))}
              </div>
            </div>
            <TextArea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder={t('webHostingAi.promptPlaceholder')}
              maxLength={maxPromptChars}
              showCount
              autoSize={{ minRows: 7, maxRows: 12 }}
              disabled={generating}
            />
            <Paragraph type="secondary">{t('webHostingAi.promptHint')}</Paragraph>
            {generating && (
              <div className="web-hosting-ai-progress">
                <Progress percent={Math.min(94, 10 + pollAttempt * 2)} status="active" showInfo={false} />
                <Text>{t('webHostingAi.processing')}</Text>
                <div className="web-hosting-ai-reasoning">
                  <div className="web-hosting-ai-progress-panel-header">
                    <Text strong>{t('webHostingAi.reasoningTitle')}</Text>
                    <Tag color="processing">LIVE</Tag>
                  </div>
                  <pre
                    ref={reasoningRef}
                    aria-live="polite"
                    data-testid="web-hosting-ai-reasoning"
                  >
                    {liveReasoning || (liveCode
                      ? t('webHostingAi.reasoningUnavailable')
                      : t('webHostingAi.waitingForReasoning'))}
                  </pre>
                </div>
                <div className="web-hosting-ai-live-code">
                  <div className="web-hosting-ai-progress-panel-header web-hosting-ai-live-code-header">
                    <Text strong>{t('webHostingAi.liveCodeTitle')}</Text>
                    <Tag color="processing">LIVE</Tag>
                  </div>
                  <pre aria-live="polite" data-testid="web-hosting-ai-live-code">
                    {liveCode || t('webHostingAi.waitingForCode')}
                  </pre>
                </div>
              </div>
            )}
            {errorMessage && (
              <Alert type="error" showIcon message={t('webHostingAi.failedTitle')} description={errorMessage} />
            )}
          </Space>
        )}
      </Modal>
      <Modal
        title={t('webHostingAi.vipCenter')}
        open={showCart}
        footer={null}
        width="80%"
        onCancel={() => setShowCart(false)}
      >
        <Cart />
      </Modal>
    </>
  );
};

export default WebHostingAiModal;
